'use client';

import {
  type FC,
  type PropsWithChildren,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';

import { isPrerenderContext } from '@/utils/prerender';
import { ScLazyLoad } from './styled';

// Global cache to store visibility state
const visibilityCache = new Map<string, boolean>();
// Evaluated once at module level — stable across renders
const isPrerender = isPrerenderContext();

interface LazyLoadProps extends PropsWithChildren {
  cacheKey: string; // Unique key for caching visibility state
  rootMargin?: string;
  threshold?: number;
  placeholderHeight?: string;
  fallback?: React.ReactNode;
}

export const LazyLoad: FC<LazyLoadProps> = ({
  cacheKey,
  children,
  rootMargin = '100px',
  threshold = 0.3,
  placeholderHeight = '500px',
  fallback = <div style={{ minHeight: '500px' }}>Loading...</div>,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  // Force all sections visible during prerender so the snapshot captures full content
  const [isVisible, setIsVisible] = useState(
    () => visibilityCache.get(cacheKey) ?? isPrerender
  );

  useEffect(() => {
    if (isVisible) return; // already visible (prerender context or cached)

    const el = ref.current;
    if (!el) return;

    let observer: IntersectionObserver | null = null;

    const timeout = setTimeout(() => {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            visibilityCache.set(cacheKey, true);
            observer?.disconnect();
          }
        },
        { root: null, rootMargin, threshold }
      );
      observer.observe(el);
    }, 100);

    return () => {
      clearTimeout(timeout);
      observer?.disconnect();
    };
  }, [cacheKey, isVisible, rootMargin, threshold]);

  return (
    <ScLazyLoad ref={ref}>
      {isVisible ? (
        <Suspense fallback={fallback}>{children}</Suspense>
      ) : (
        <div style={{ minHeight: placeholderHeight }} />
      )}
    </ScLazyLoad>
  );
};
